import { addBusinessDays, addDays, format, parseISO } from 'date-fns';
import { evaluateP2PAppeal } from './evaluateP2PAppeal';
import { P2P_ENABLED_ENTITLEMENT } from './entitlements';
import type {
  P2PAppealEvaluationInput,
  P2PEvaluationResult,
} from './types';
import type {
  P2PDemoAppealView,
  P2PDemoEvidenceFinding,
  P2PDemoScenario,
  P2PDemoScenarioId,
  P2PDemoSession,
  P2PDemoView,
} from './vcDemoTypes';

const PATRICIA_WILDER: P2PDemoScenario = {
  id: 'patricia-wilder',
  caseId: 'demo-ortho-scheduled-wilder-left-tka',
  patientName: 'Patricia Wilder',
  patientDob: '1961-09-29',
  patientMrn: 'DEMO-TKA-1001',
  payerName: 'Anthem BCBS',
  umVendor: 'Carelon',
  lineOfBusiness: 'Commercial PPO',
  procedureName: 'Left total knee arthroplasty',
  cptCodes: ['27447', '27435-XS'],
  laterality: 'left',
  policyName: 'Anthem / Carelon Orthopedic Surgery Matrix',
  denialRationale:
    'The submitted record does not establish the required 24 weeks of conservative management for a member with BMI greater than 40 and does not document advanced radiographic osteoarthritis.',
  denialCodes: ['CARC 50', 'RARC N115'],
  vendorAlert:
    'Confirm the Carelon reviewer is authorized to reverse the determination and request the authorization reference number before ending the call.',
  reviewerSpecialty: 'Adult reconstruction / total joint arthroplasty',
  p2pWindowBusinessDays: 5,
  strategy: {
    type: 'direct_match',
    label: 'Direct policy match found',
    summary: 'The submitted packet contains both disputed elements: more than 24 weeks of multimodal care and Kellgren-Lawrence Grade 4 disease.',
  },
  evidence: [
    {
      id: 'wilder-conservative-care',
      label: 'Conservative management',
      clinicalFact: 'More than 24 weeks of supervised therapy, home exercise, bracing, medication, weight-loss counseling, corticosteroid injection, and viscosupplementation are documented.',
      policyThreshold: 'BMI greater than 40 requires 24 weeks of conservative management.',
      status: 'met',
      source: 'Submitted conservative-care summary',
    },
    {
      id: 'wilder-radiographs',
      label: 'Radiographic severity',
      clinicalFact: 'Weight-bearing radiographs document Kellgren-Lawrence Grade 4 tricompartmental bone-on-bone disease with varus deformity.',
      policyThreshold: 'Elective TKA requires Kellgren-Lawrence Grade 3 or 4.',
      status: 'met',
      source: 'Submitted weight-bearing X-ray report, 05/14/2026',
    },
    {
      id: 'wilder-function',
      label: 'Objective functional loss',
      clinicalFact: 'Antalgic gait, cane use, walking tolerance under one block, stair limitation, night pain, and difficulty rising from a chair are documented.',
      policyThreshold: 'Persistent pain and function-limiting osteoarthritis despite nonoperative care.',
      status: 'met',
      source: 'Submitted orthopedic evaluation, 05/18/2026',
    },
    {
      id: 'wilder-clearance',
      label: 'Medical optimization',
      clinicalFact: 'HbA1c is 6.4%, cotinine is 4 ng/mL, and cardiology, airway, DVT, and hospital-monitoring plans are documented.',
      policyThreshold: 'Medical clearance and perioperative risk mitigation must be complete.',
      status: 'met',
      source: 'Submitted clearance and laboratory packet, 05/28/2026',
    },
  ],
  evidencePayload: [
    {
      id: 'conservative_therapy',
      label: 'Conservative Therapy',
      summary: 'More than 24 weeks of supervised therapy, home exercise, bracing, medication, weight-loss counseling, corticosteroid injection, and viscosupplementation are documented.',
      sourceIds: ['wilder-conservative-care'],
    },
    {
      id: 'objective_deficit',
      label: 'Objective Deficit',
      summary: 'Antalgic gait, cane dependence, walking tolerance under one block, night pain, stair limitation, and difficulty rising from a chair remain documented.',
      sourceIds: ['wilder-function'],
    },
    {
      id: 'radiographic_proof',
      label: 'Radiographic Proof',
      summary: 'Weight-bearing radiographs show Kellgren-Lawrence Grade 4 tricompartmental bone-on-bone disease with varus deformity.',
      sourceIds: ['wilder-radiographs'],
    },
  ],
  callPhases: [
    {
      id: 'credentials',
      number: '01',
      title: 'Establish Credentials & Vendor Traps',
      scriptLabel: 'The Opening Move',
      script: 'Before we discuss the patient, please state your full name, board certification, and orthopedic sub-specialty for the case record. Please also confirm that you have authority to reverse this determination today.',
    },
    {
      id: 'rebuttal',
      number: '02',
      title: 'The Policy Rebuttal Script',
      scriptLabel: 'Script',
      script: 'The denial states that 24 weeks of conservative treatment and advanced radiographic disease were not established. Both elements are present in the submitted packet: more than 24 weeks of multimodal treatment and Kellgren-Lawrence Grade 4 weight-bearing radiographs.',
    },
    {
      id: 'evidence',
      number: '03',
      title: 'The Clinical Evidence Payload',
      scriptLabel: 'Evidence Summary',
      script: 'The record also documents an antalgic gait, cane dependence, walking tolerance under one block, night pain, failed injections, HbA1c 6.4%, nicotine clearance, and completed medical risk mitigation for BMI 44.',
    },
    {
      id: 'demand',
      number: '04',
      title: 'The Actionable Demand',
      scriptLabel: 'Script',
      script: 'The submitted documentation satisfies the stated pathway. I am requesting reversal of the denial and an actionable authorization reference number on this call.',
    },
  ],
  appeal: {
    title: 'Pre-Service Clinical Appeal',
    pathway: 'Pre-Service Clinical Appeal',
    pathwayLabel: 'Pathway A: Pre-Service Clinical Appeal',
    trigger: 'Automatically compile when the P2P is recorded as Failed or Timed Out.',
    deadlineDays: 60,
    deadlineRule: 'Submit within 60 calendar days of the authorization denial under the configured demo matrix.',
    requirement: 'Compile symptom duration, objective examination, conservative-care history, radiographic severity, optimization evidence, and surgical rationale into one payer-ready appeal.',
    decisionWindow: 'Expedited review available when supported by the submitted clinical record',
    subject: 'Urgent pre-service appeal - left total knee arthroplasty, CPT 27447',
    recipient: 'Anthem BCBS / Carelon Appeals Department',
    openingArgument: 'I am formally appealing the prior authorization denial for Patricia Wilder\'s left total knee arthroplasty. The denial incorrectly states that the submitted record fails to establish the escalated conservative-management duration and advanced radiographic osteoarthritis.',
    treatmentArgument: 'The submitted packet documents more than 24 weeks of supervised therapy and multimodal nonoperative treatment, including home exercise, bracing, medication, weight-loss counseling, corticosteroid injection, and viscosupplementation, without durable functional improvement.',
    clinicalArgument: 'Weight-bearing radiographs establish Kellgren-Lawrence Grade 4 tricompartmental bone-on-bone disease with varus deformity. Examination and functional history document an antalgic gait, cane dependence, walking tolerance under one block, night pain, stair limitation, and difficulty rising from a chair.',
    delayRiskArgument: 'Further delay would prolong severe pain and mobility loss despite exhaustion of the configured conservative pathway. The record also documents medical optimization, including HbA1c 6.4%, nicotine clearance, cardiology review, and perioperative risk mitigation.',
    requestedOutcome: 'Reverse the denial and issue immediate authorization for the requested left total knee arthroplasty.',
  },
};

const THOMAS_PALMER: P2PDemoScenario = {
  id: 'thomas-palmer',
  caseId: 'demo-ortho-scheduled-palmer-rotator-cuff',
  patientName: 'Thomas Palmer',
  patientDob: '1945-07-15',
  patientMrn: 'DEMO-SH-1002',
  payerName: 'UnitedHealthcare',
  umVendor: 'InterQual',
  lineOfBusiness: 'Commercial',
  procedureName: 'Left rotator cuff repair with distal clavicle excision',
  cptCodes: ['23420', '23120-XS'],
  laterality: 'left',
  policyName: 'UnitedHealthcare / InterQual Coding Validation Matrix',
  denialRationale:
    'The requested procedure lines do not demonstrate strict left-laterality alignment between the authorization, diagnosis set, and billed modifiers.',
  denialCodes: ['CARC 4', 'RARC N517'],
  vendorAlert:
    'Ask the reviewer to identify the exact line-level laterality mismatch and confirm whether corrected coding can be accepted during the P2P window.',
  reviewerSpecialty: 'Shoulder and elbow surgery',
  p2pWindowBusinessDays: 5,
  strategy: {
    type: 'correctable_defect',
    label: 'Correctable coding defect isolated',
    summary: 'The clinical record is consistently left-sided. LINK can normalize modifier LT while preserving XS on the distinct AC-joint procedure.',
  },
  evidence: [
    {
      id: 'palmer-laterality',
      label: 'Laterality alignment',
      clinicalFact: 'The submitted clinical record consistently identifies the left shoulder, but the primary authorization line requires an explicit LT modifier.',
      policyThreshold: 'Authorization, diagnosis, and procedure-line laterality must match exactly.',
      status: 'blocked',
      source: 'Submitted authorization coding snapshot',
      remediation: 'Apply LT to the primary CPT line and reevaluate before the call.',
    },
    {
      id: 'palmer-distinct-structure',
      label: 'Distinct anatomic structure',
      clinicalFact: 'The distal clavicle excision treats symptomatic AC-joint arthrosis separate from the rotator cuff footprint and outlet work.',
      policyThreshold: 'Secondary procedure requires distinct anatomic support.',
      status: 'met',
      source: 'Submitted MRI, X-ray, and orthopedic evaluation',
    },
    {
      id: 'palmer-conservative-care',
      label: 'Conservative management',
      clinicalFact: 'More than 12 weeks of supervised therapy, injection, medication management, and activity modification failed.',
      policyThreshold: 'Documented failure of nonoperative care.',
      status: 'met',
      source: 'Submitted conservative-care summary',
    },
  ],
  evidencePayload: [
    {
      id: 'conservative_therapy',
      label: 'Conservative Therapy',
      summary: 'More than 12 weeks of supervised therapy, injection, medication management, and activity modification failed to restore function.',
      sourceIds: ['palmer-conservative-care'],
    },
    {
      id: 'objective_deficit',
      label: 'Objective Deficit',
      summary: 'The submitted evaluation localizes symptomatic cuff and AC-joint pathology to separate left-shoulder structures.',
      sourceIds: ['palmer-distinct-structure'],
    },
    {
      id: 'radiographic_proof',
      label: 'Radiographic Proof',
      summary: 'MRI and X-ray support a supraspinatus tear and symptomatic AC-joint arthrosis as distinct anatomic work.',
      sourceIds: ['palmer-distinct-structure'],
    },
  ],
  callPhases: [
    {
      id: 'credentials',
      number: '01',
      title: 'Establish Credentials & Vendor Traps',
      scriptLabel: 'The Opening Move',
      script: 'Please state your name, board certification, and shoulder sub-specialty. Please identify the exact procedure line that triggered the laterality denial.',
    },
    {
      id: 'rebuttal',
      number: '02',
      title: 'The Policy Rebuttal Script',
      scriptLabel: 'Script',
      script: 'The submitted clinical record, diagnoses, and imaging consistently identify the left shoulder. The primary procedure line has now been normalized with modifier LT while XS remains attached to the distinct AC-joint procedure.',
    },
    {
      id: 'evidence',
      number: '03',
      title: 'The Clinical Evidence Payload',
      scriptLabel: 'Evidence Summary',
      script: 'MRI and examination support separate pathology: the cuff repair treats the supraspinatus tear, while distal clavicle excision treats symptomatic AC-joint arthrosis as a distinct anatomic structure.',
    },
    {
      id: 'demand',
      number: '04',
      title: 'The Actionable Demand',
      scriptLabel: 'Script',
      script: 'With laterality aligned and distinct-structure evidence preserved, please reverse the coding denial and issue the authorization reference number.',
    },
  ],
  appeal: {
    title: 'Pre-Service Coding and Clinical Appeal',
    pathway: 'Pre-Service Clinical Appeal',
    pathwayLabel: 'Pathway A: Pre-Service Clinical Appeal',
    trigger: 'Automatically compile when the corrected P2P is recorded as Failed or Timed Out.',
    deadlineDays: 60,
    deadlineRule: 'Submit within 60 calendar days of the authorization denial under the configured demo matrix.',
    requirement: 'Compile the corrected procedure-line laterality, distinct-structure support, conservative-care history, and requested coding disposition.',
    decisionWindow: 'Decision requested within the applicable pre-service appeal window',
    subject: 'Pre-service appeal - left shoulder CPT 23420 and 23120',
    recipient: 'UnitedHealthcare / InterQual Clinical Appeals',
    openingArgument: 'I am formally appealing the prior authorization denial for Thomas Palmer\'s left shoulder repair. The denial is based on a procedure-line laterality mismatch rather than an absence of clinical necessity.',
    treatmentArgument: 'The submitted record documents more than 12 weeks of supervised therapy, injection, medication management, and activity modification without adequate relief.',
    clinicalArgument: 'The clinical evaluation, diagnoses, and imaging consistently identify the left shoulder. The corrected primary line applies modifier LT to CPT 23420, while modifier XS remains attached to the separate distal clavicle procedure supported by symptomatic AC-joint arthrosis.',
    delayRiskArgument: 'Maintaining a denial after the line-level correction would elevate a resolved coding defect over the consistently documented clinical record and delay definitive treatment of separate cuff and AC-joint pathology.',
    requestedOutcome: 'Accept the corrected laterality alignment, reverse the denial, and issue authorization for the requested left shoulder procedures.',
  },
};

const SARAH_JENKINS: P2PDemoScenario = {
  id: 'sarah-jenkins',
  caseId: 'demo-ortho-scheduled-sarah-jenkins-rotator-cuff',
  patientName: 'Sarah Jenkins',
  patientDob: '1968-08-14',
  patientMrn: 'DEMO-RC-2045',
  payerName: 'Anthem BCBS',
  umVendor: 'Carelon',
  lineOfBusiness: 'Commercial PPO',
  procedureName: 'Right arthroscopic rotator cuff repair',
  cptCodes: ['29827-RT', '29826-XS', '29828-XS'],
  laterality: 'right',
  policyName: 'Anthem / Carelon Orthopedic Surgery Matrix',
  denialRationale:
    'The submitted record does not establish the escalated conservative-management duration required for a member with BMI greater than 40.',
  denialCodes: ['CARC 50', 'RARC N115'],
  vendorAlert:
    'This is Anthem / Carelon. They are applying the relative-contraindication trap: BMI greater than 40 escalates conservative care to 24 weeks. Do not debate obesity. State the documented duration accurately, then pivot to the structural-severity exception.',
  reviewerSpecialty: 'Shoulder and elbow surgery',
  p2pWindowBusinessDays: 5,
  strategy: {
    type: 'policy_exception',
    label: 'Structural severity exception found',
    summary: 'The 24-week duration is not met. LINK instead maps the retracted full-thickness tear, persistent 3/5 weakness, and major ADL loss to the configured structural-exception pathway.',
  },
  evidence: [
    {
      id: 'jenkins-therapy-duration',
      label: 'Conservative management duration',
      clinicalFact: 'Eight weeks and 16 sessions of supervised physical therapy are documented.',
      policyThreshold: 'BMI greater than 40 escalates the conservative-care duration to 24 weeks.',
      status: 'warning',
      resultLabel: '8 of 24 weeks documented',
      source: 'Submitted PT discharge summary, 04/26/2026',
      remediation: 'Do not claim the duration threshold is met. Use the separately supported structural-severity exception.',
    },
    {
      id: 'jenkins-imaging',
      label: 'Advanced imaging',
      clinicalFact: 'MRI documents a full-thickness full-width supraspinatus tear with 2 cm retraction, partial infraspinatus tearing, and biceps instability.',
      policyThreshold: 'Advanced imaging must support repairable structural pathology.',
      status: 'met',
      source: 'Submitted MRI report, 05/16/2026',
    },
    {
      id: 'jenkins-deficit',
      label: 'Objective deficit',
      clinicalFact: 'Active elevation is limited and supraspinatus and external-rotation strength remain 3/5 with major ADL loss.',
      policyThreshold: 'Objective weakness and functional limitation must be documented.',
      status: 'met',
      source: 'Submitted orthopedic evaluation and PT discharge summary',
    },
    {
      id: 'jenkins-structural-exception',
      label: 'Structural severity exception',
      clinicalFact: 'A retracted full-thickness tear, persistent 3/5 cuff weakness, limited active elevation, and major ADL loss support the configured exception from prolonged therapy.',
      policyThreshold: 'Structural failure with severe objective functional loss may override the escalated duration in this fixed demo matrix.',
      status: 'exception',
      resultLabel: 'Exception applies',
      source: 'Submitted MRI, orthopedic evaluation, and PT discharge summary',
    },
  ],
  evidencePayload: [
    {
      id: 'conservative_therapy',
      label: 'Conservative Therapy',
      summary: 'Eight weeks and 16 sessions of supervised physical therapy are documented, with a plateau and persistent severe functional deficit despite medication, injection, home exercise, and activity modification.',
      sourceIds: ['jenkins-therapy-duration'],
    },
    {
      id: 'objective_deficit',
      label: 'Objective Deficit',
      summary: 'Active elevation is limited; supraspinatus and external-rotation strength are 3/5; the patient cannot reach above shoulder height, dress normally, style her hair, or perform sustained shoulder-level work.',
      sourceIds: ['jenkins-deficit'],
    },
    {
      id: 'radiographic_proof',
      label: 'Radiographic Proof',
      summary: 'MRI confirms a full-thickness, full-width supraspinatus tear with 2 cm medial retraction, high-grade partial infraspinatus tearing, and long-head biceps instability.',
      sourceIds: ['jenkins-imaging'],
    },
  ],
  callPhases: [
    {
      id: 'credentials',
      number: '01',
      title: 'Establish Credentials & Vendor Traps',
      scriptLabel: 'The Opening Move',
      script: 'Before we discuss the patient, please state your full name, board certification, and specific orthopedic sub-specialty for my official medical record documentation.',
    },
    {
      id: 'rebuttal',
      number: '02',
      title: 'The Policy Rebuttal Script',
      scriptLabel: 'Script',
      script: 'Your denial states the record does not establish 24 weeks of conservative management due to a BMI greater than 40. However, under the configured Anthem / Carelon Orthopedic Surgery Matrix, the structural-severity exception applies when documented structural failure, severe functional loss, and objective weakness make prolonged conservative therapy clinically inappropriate. The submitted record meets that exact exception pathway.',
    },
    {
      id: 'evidence',
      number: '03',
      title: 'The Clinical Evidence Payload',
      scriptLabel: 'Evidence Summary',
      script: 'The record documents eight weeks and 16 supervised sessions, persistent 3/5 cuff weakness and limited active elevation, major ADL loss, and a full-thickness full-width supraspinatus tear with 2 cm retraction, partial infraspinatus tearing, and biceps instability.',
    },
    {
      id: 'demand',
      number: '04',
      title: 'The Actionable Demand',
      scriptLabel: 'Script',
      script: 'The documentation satisfies the configured medical-necessity pathway through the structural-severity exception; the 24-week duration is not the controlling criterion for this retracted full-thickness tear with severe objective loss. I am requesting an immediate, actionable authorization on this call. Please provide the authorization reference number now.',
    },
  ],
  appeal: {
    title: 'Pre-Service Clinical Appeal',
    pathway: 'Pre-Service Clinical Appeal',
    pathwayLabel: 'Pathway A: Pre-Service Clinical Appeal',
    trigger: 'Automatically compile when the medical director refuses to overturn the denial or the P2P window expires.',
    deadlineDays: 60,
    deadlineRule: 'Submit within 60 calendar days of the authorization denial under the configured demo matrix.',
    requirement: 'Detail symptom duration, objective examination, the complete conservative-treatment record, structural imaging, and the evidence-based rationale for timely repair.',
    decisionWindow: 'Expedited review may be requested when the submitted record certifies a severe threat to maximum function',
    subject: 'Pre-Service Clinical Appeal for Sarah Jenkins - CPT 29827-RT, 29826-XS, 29828-XS',
    recipient: 'Anthem BCBS / Carelon Appeals Department',
    openingArgument: 'I am formally appealing the prior authorization denial for Sarah Jenkins\'s right arthroscopic rotator cuff repair. The denial strictly applies a 24-week conservative-therapy timeline based on BMI greater than 40 without accounting for the patient\'s overriding structural deficits, which independently satisfy the configured structural-severity exception.',
    treatmentArgument: 'The patient completed eight weeks and 16 sessions of supervised physical therapy and reached a documented plateau. She also failed medication, home exercise, activity modification, and a subacromial corticosteroid injection that produced only transient relief. The appeal does not represent that 24 weeks were completed; it relies on the separately documented exception pathway.',
    clinicalArgument: 'MRI confirms a full-thickness, full-width supraspinatus tear with 2 cm medial retraction, high-grade partial infraspinatus tearing, and long-head biceps instability. Examination documents limited active elevation, 3/5 supraspinatus and external-rotation strength, severe nocturnal pain, and major loss of activities of daily living.',
    delayRiskArgument: 'Requiring an additional 16 weeks of therapy after a documented plateau would delay definitive treatment of a retracted full-thickness tear and create avoidable risk of tear progression, muscle atrophy, and further loss of function. Timely repair is medically necessary based on the combined structural and objective deficits in the submitted record.',
    requestedOutcome: 'Reverse the denial immediately and authorize CPT 29827-RT, 29826-XS, and 29828-XS for the documented right-shoulder pathology.',
  },
};

export const P2P_DEMO_SCENARIOS: readonly P2PDemoScenario[] = [
  PATRICIA_WILDER,
  THOMAS_PALMER,
  SARAH_JENKINS,
];

export const DEFAULT_P2P_DEMO_SCENARIO_ID: P2PDemoScenarioId = 'patricia-wilder';

export function getP2PDemoScenario(id: P2PDemoScenarioId): P2PDemoScenario {
  return P2P_DEMO_SCENARIOS.find((scenario) => scenario.id === id) ?? PATRICIA_WILDER;
}

export function getP2PDemoScenarioForCase(caseId: string): P2PDemoScenario | null {
  return P2P_DEMO_SCENARIOS.find((scenario) => scenario.caseId === caseId) ?? null;
}

function baseDate(session: P2PDemoSession): Date {
  return parseISO(session.denialReceivedAt || session.simulatedAt || '2026-07-13T14:00:00.000Z');
}

function buildSubmittedEvaluationInput(session: P2PDemoSession): P2PAppealEvaluationInput {
  const scenario = getP2PDemoScenario(session.scenarioId);
  const denialDate = session.denialReceivedAt || session.simulatedAt || '2026-07-13T14:00:00.000Z';
  const p2pDeadline = addBusinessDays(parseISO(denialDate), scenario.p2pWindowBusinessDays).toISOString();

  return {
    entitlement: P2P_ENABLED_ENTITLEMENT,
    workflowPhase: 'denial_review',
    requestKind: 'pre_auth',
    requestSubmittedAt: session.submittedAt,
    payerResponseDate: denialDate,
    authorizationStatus: 'payer_denied',
    payerNormalizedStatus: 'DENIED',
    payerResponseStatus: 'denied',
    denialText: `${scenario.denialRationale} Peer-to-peer review is available.`,
    p2pStatus: 'requested',
    caseId: scenario.caseId,
    patient: {
      name: scenario.patientName,
      dob: scenario.patientDob,
      mrn: scenario.patientMrn,
    },
    payer: {
      name: scenario.payerName,
      plan: scenario.lineOfBusiness,
    },
    surgeon: {
      name: 'Dr. John J. Larkin, MD',
      plan: scenario.reviewerSpecialty,
    },
    facility: {
      name: 'St. Elizabeth Florence',
    },
    submittedArtifact: {
      id: `submitted-${scenario.caseId}`,
      type: 'pre_auth_packet',
      submittedAt: session.submittedAt || denialDate,
      title: 'Submitted authorization packet snapshot',
      summaryText: `${scenario.procedureName} (${scenario.cptCodes.join(', ')}) with ${scenario.evidence.filter((item) => item.status === 'met').length} policy-supported evidence findings.`,
      evidenceFacts: scenario.evidence.map((finding) => ({
        id: finding.id,
        label: finding.label,
        value: finding.clinicalFact,
        source: 'submitted_packet' as const,
      })),
    },
    payerResponse: {
      denialText: scenario.denialRationale,
      responseDate: denialDate,
      p2pStatus: 'requested',
      deadline: p2pDeadline,
    },
  };
}

export function evaluateP2PDemoActivation(session: P2PDemoSession): P2PEvaluationResult {
  return evaluateP2PAppeal(buildSubmittedEvaluationInput(session));
}

function evidenceForSession(session: P2PDemoSession): P2PDemoEvidenceFinding[] {
  const scenario = getP2PDemoScenario(session.scenarioId);
  if (scenario.id !== 'thomas-palmer' || !session.codingCorrectionApplied) {
    return scenario.evidence;
  }

  return scenario.evidence.map((finding) => finding.id === 'palmer-laterality'
    ? {
        ...finding,
        status: 'met',
        clinicalFact: 'The submitted left-shoulder evidence is now aligned with modifier LT on primary CPT 23420; modifier XS remains on the distinct AC-joint procedure.',
        source: 'Corrected submitted authorization coding snapshot',
        remediation: undefined,
      }
    : finding);
}

function buildAppeal(session: P2PDemoSession, evidence: P2PDemoEvidenceFinding[]): P2PDemoAppealView | null {
  const scenario = getP2PDemoScenario(session.scenarioId);
  if (!scenario.appeal || !session.outcome || session.outcome === 'overturned') return null;
  if (evidence.some((finding) => finding.status === 'blocked')) return null;

  const denialDate = baseDate(session);
  const submissionDeadline = addDays(denialDate, scenario.appeal.deadlineDays);
  const evidenceNarrative = evidence
    .map((finding, index) => `${index + 1}. ${finding.label}: ${finding.clinicalFact} Source: ${finding.source}.`)
    .join('\n');
  const outcomeLabel = session.outcome === 'timed_out'
    ? 'timed out without an actionable determination'
    : 'did not reverse the denial';

  return {
    title: scenario.appeal.title,
    pathway: scenario.appeal.pathway,
    submissionDeadline: submissionDeadline.toISOString(),
    decisionWindow: scenario.appeal.decisionWindow,
    subject: scenario.appeal.subject,
    body: `Date: ${format(denialDate, 'MMMM d, yyyy')}\n\nTo: ${scenario.appeal.recipient}\nRe: ${scenario.appeal.subject}\nPatient: ${scenario.patientName} | DOB ${format(parseISO(scenario.patientDob), 'MM/dd/yyyy')} | MRN ${scenario.patientMrn}\nDenied procedure: ${scenario.procedureName} (${scenario.cptCodes.join(', ')})\nDenial reference: ${scenario.denialCodes.join(' / ')}\n\nTo the Reviewing Medical Director:\n\n${scenario.appeal.openingArgument}\n\nThe peer-to-peer review ${outcomeLabel}. ${scenario.appeal.treatmentArgument}\n\n${scenario.appeal.clinicalArgument}\n\n${scenario.appeal.delayRiskArgument}\n\nSubmitted evidence relied upon:\n${evidenceNarrative}\n\n${scenario.appeal.requestedOutcome} Please provide the written determination and authorization reference number.\n\nSincerely,\nDr. John J. Larkin, MD\nOperating Surgeon\nSt. Elizabeth Florence`,
  };
}

export function buildP2PDemoView(session: P2PDemoSession): P2PDemoView {
  const scenario = getP2PDemoScenario(session.scenarioId);
  const denialDate = baseDate(session);
  const p2pDeadline = addBusinessDays(denialDate, scenario.p2pWindowBusinessDays);
  const evidence = evidenceForSession(session);
  const escalationDeadline = scenario.appeal
    ? addDays(denialDate, scenario.appeal.deadlineDays)
    : null;

  return {
    scenario,
    p2pDeadline: p2pDeadline.toISOString(),
    p2pDeadlineLabel: format(p2pDeadline, "EEE, MMM d 'at' h:mm a"),
    denialDateLabel: format(denialDate, 'MMM d, yyyy'),
    escalationDeadline: escalationDeadline?.toISOString() ?? null,
    escalationDeadlineLabel: escalationDeadline
      ? format(escalationDeadline, 'MMM d, yyyy')
      : null,
    evidence,
    blocked: evidence.some((finding) => finding.status === 'blocked'),
    appeal: buildAppeal(session, evidence),
  };
}

export function buildP2PDemoCheatsheet(view: P2PDemoView): string {
  const { scenario } = view;
  const phase = (id: P2PDemoScenario['callPhases'][number]['id']) =>
    scenario.callPhases.find((item) => item.id === id)!;
  const phaseOne = phase('credentials');
  const phaseTwo = phase('rebuttal');
  const phaseFour = phase('demand');
  const evidencePayload = scenario.evidencePayload
    .map((item) => `* ${item.label}: ${item.summary}`)
    .join('\n');
  const escalation = scenario.appeal
    ? `\n\nPHASE 5: AUTOMATED ESCALATION & FORMAL APPEAL GENERATION\nTrigger: ${scenario.appeal.trigger}\nEscalation Pathway Identified: ${scenario.appeal.pathwayLabel}\nSubmission Deadline: ${scenario.appeal.deadlineRule}${view.escalationDeadlineLabel ? ` Deadline: ${view.escalationDeadlineLabel}.` : ''}\nRequirement: ${scenario.appeal.requirement}${view.appeal ? `\n\n[Generated Appeal Draft]\n${view.appeal.body}` : ''}`
    : '';

  return `P2P TACTICAL CHEATSHEET & ESCALATION PROTOCOL\n\nPayer / UM Vendor: ${scenario.payerName} / ${scenario.umVendor}\nDenied Procedure: ${scenario.cptCodes.join(', ')} (${scenario.procedureName})\nReported Denial Rationale: ${scenario.denialCodes.join(' / ')} - ${scenario.denialRationale}\n\nPHASE 1: ${phaseOne.title.toUpperCase()}\n${phaseOne.scriptLabel}: "${phaseOne.script}"\nVendor Alert: ${scenario.vendorAlert}\n\nPHASE 2: ${phaseTwo.title.toUpperCase()}\n${phaseTwo.scriptLabel}: "${phaseTwo.script}"\n\nPHASE 3: THE CLINICAL EVIDENCE PAYLOAD\n${evidencePayload}\n\nPHASE 4: ${phaseFour.title.toUpperCase()}\n${phaseFour.scriptLabel}: "${phaseFour.script}"${escalation}`;
}
